import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { useAuth } from '../AuthContext';
import { UserProfile } from '../types';

interface FaceLoginModalProps {
    isOpen: boolean;
    onClose: () => void;
    mode: 'register' | 'login';
    onSuccess: (user?: UserProfile) => void;
    onError: (message: string) => void;
}

const FaceLoginModal: React.FC<FaceLoginModalProps> = ({ isOpen, onClose, mode, onSuccess, onError }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [cameraError, setCameraError] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [feedback, setFeedback] = useState('Position your face in the oval');

    const { registerFaceLogin, loginWithFace } = useAuth();

    const stopStream = useCallback(() => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
    }, [stream]);

    const startCamera = useCallback(async () => {
        stopStream();
        setCameraError(null);
        setFeedback('Position your face in the oval');
        try {
            const newStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'user',
                    width: { ideal: 640 },
                    height: { ideal: 480 }
                }
            });
            if (videoRef.current) {
                videoRef.current.srcObject = newStream;
            }
            setStream(newStream);
        } catch (err) {
            console.error("Error accessing camera:", err);
            setCameraError("Could not access camera. Please check your browser permissions.");
        }
    }, [stopStream]);

    useEffect(() => {
        if (isOpen) {
            startCamera();
        } else {
            stopStream();
        }
        return stopStream;
    }, [isOpen, startCamera, stopStream]);

    const capturePhoto = (): string | null => {
        if (!videoRef.current || !canvasRef.current) return null;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext('2d');
        if (context) {
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            return canvas.toDataURL('image/jpeg');
        }
        return null;
    };

    const handleScan = async () => {
        setIsProcessing(true);
        setFeedback('Scanning...');
        const imageData = capturePhoto();

        if (!imageData) {
            onError("Failed to capture image.");
            setIsProcessing(false);
            return;
        }

        // Simulate a network delay
        await new Promise(res => setTimeout(res, 1500));

        if (mode === 'register') {
            try {
                await registerFaceLogin(imageData);
                setFeedback('Success!');
                setTimeout(() => onSuccess(), 1000);
            } catch (e: any) {
                setFeedback('Registration Failed');
                setTimeout(() => onError(e.message || "Could not register face."), 1000);
            }
        } else { // mode === 'login'
            try {
                const loggedInUser = await loginWithFace(imageData);
                if (loggedInUser) {
                    setFeedback(`Welcome, ${loggedInUser.displayName}!`);
                    setTimeout(() => onSuccess(loggedInUser), 1000);
                } else {
                    setFeedback('Face Not Recognized');
                    setTimeout(() => {
                        onError("Face not recognized. Please try again or use your display name.");
                        startCamera(); // Restart camera for another try
                    }, 2000);
                }
            } catch (e: any) {
                setFeedback('Login Failed');
                setTimeout(() => onError(e.message || "An error occurred during login."), 1000);
            }
        }
        setIsProcessing(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 z-[70] flex flex-col items-center justify-center p-4" onClick={onClose}>
            <div className="relative w-full max-w-sm aspect-[3/4] bg-gray-900 rounded-2xl overflow-hidden shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
                {cameraError ? (
                    <div className="flex flex-col items-center justify-center h-full text-white p-4">
                        <i className="ph-duotone ph-warning-circle text-5xl text-red-500 mb-4"></i>
                        <h2 className="text-xl font-bold mb-2">Camera Error</h2>
                        <p className="text-center">{cameraError}</p>
                        <button onClick={onClose} className="mt-6 bg-[var(--primary-dark-purple)] font-bold py-2 px-4 rounded-lg">Close</button>
                    </div>
                ) : (
                    <>
                        <video ref={videoRef} autoPlay playsInline className="absolute inset-0 w-full h-full object-cover" />
                        <canvas ref={canvasRef} className="hidden" />
                        
                        {/* Overlay */}
                        <div className="absolute inset-0 flex items-center justify-center">
                             <div className="w-[65%] aspect-[3/4] border-4 border-dashed border-white/50 rounded-[50%] box-content" style={{ boxShadow: '0 0 0 100vmax rgba(0,0,0,0.7)' }}></div>
                        </div>

                        {/* UI */}
                        <div className="relative z-10 flex flex-col justify-between h-full p-6 text-center">
                            <div/>
                            <div className="flex flex-col items-center">
                                <p className="text-white text-lg font-semibold bg-black/50 px-4 py-1 rounded-full">{feedback}</p>
                            </div>
                            
                            <div className="flex justify-center items-center">
                                {isProcessing ? (
                                    <div className="spinner-white h-20 w-20 border-4"></div>
                                ) : (
                                    <button onClick={handleScan} className="w-20 h-20 rounded-full bg-white border-4 border-black/30 transition-transform hover:scale-105 active:scale-95"></button>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
             <button onClick={onClose} className="mt-4 bg-transparent text-white font-semibold py-2 px-4 rounded-lg border border-white/50 hover:bg-white/10">Cancel</button>
        </div>
    );
};

export default FaceLoginModal;